import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

import '@mantine/charts/styles.css';

import './app.scss';
import './tailwind.css';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { Route, BrowserRouter as Router, Routes } from 'react-router';

import './i18n';

import {
    ColorSchemeScript,
    localStorageColorSchemeManager,
    MantineProvider,
    useComputedColorScheme,
} from '@mantine/core';
import { Notifications } from '@mantine/notifications';

import { OMISE_PUBLIC_KEY } from './constants/env';
import { initChatwootSDK } from './hooks/useChatwoot';
import {
    AuthenticationPage,
    CloudComputeTerminalPage,
    EmailVerificationPage,
    GitHubOAuthCallbackPage,
} from './pages';
import { ProjectProvider, UserProvider } from './providers';
import Root from './Root';
import store from './store';
import { mantineCssVariablesResolver, mantineTheme } from './theme/mantine.theme';

import { ErrorBoundary } from './components/Organisms';

import type { Omise, OmiseCard } from 'omise-js-typed';
import QueryProvider from './providers/QueryProvider';
declare global {
    interface Window {
        Omise: Omise;
        OmiseCard: OmiseCard;
    }
}

window.Omise?.setPublicKey(OMISE_PUBLIC_KEY);
window.OmiseCard?.configure({
    publicKey: OMISE_PUBLIC_KEY,
});

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const ChatwootInit = () => {
    useEffect(() => {
        initChatwootSDK();
    }, []);

    return null;
};

const ThemeColorMetaSync = () => {
    const computedColorScheme = useComputedColorScheme('dark');

    useEffect(() => {
        document
            .querySelector('meta[name="theme-color"]')
            ?.setAttribute('content', computedColorScheme === 'light' ? '#fafafa' : '#111111');
    }, [computedColorScheme]);

    return null;
};

const colorSchemeManager = localStorageColorSchemeManager({ key: 'xver-color-scheme' });

const root = createRoot(document.getElementById('root')!);
root.render(
    <React.StrictMode>
        <ColorSchemeScript defaultColorScheme="auto" localStorageKey="xver-color-scheme" />
        <ErrorBoundary>
            <>
                <ChatwootInit />
                <MantineProvider
                    defaultColorScheme="auto"
                    colorSchemeManager={colorSchemeManager}
                    theme={mantineTheme}
                    cssVariablesResolver={mantineCssVariablesResolver}
                >
                    <ThemeColorMetaSync />
                    <Notifications
                        limit={5}
                        position={'bottom-right'}
                        autoClose={6000}
                        zIndex={1024}
                    />

                    <Provider store={store}>
                        <Router>
                            <QueryProvider>
                                <UserProvider>
                                    <Suspense fallback={null}>
                                        <ProjectProvider>
                                            <Routes>
                                                <Route path="/" element={<AuthenticationPage />} />
                                                <Route
                                                    path="/authentication/email-verification"
                                                    element={<EmailVerificationPage />}
                                                />
                                                <Route
                                                    path="/oauth/github/callback"
                                                    element={<GitHubOAuthCallbackPage />}
                                                />
                                                <Route
                                                    path="/cloud-compute/:instanceId/terminal"
                                                    element={<CloudComputeTerminalPage />}
                                                />
                                                <Route path="/*" element={<Root />} />
                                            </Routes>
                                        </ProjectProvider>
                                    </Suspense>
                                </UserProvider>
                            </QueryProvider>
                        </Router>
                    </Provider>
                </MantineProvider>
            </>
        </ErrorBoundary>
    </React.StrictMode>
);
